import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Heart, Eye, Sparkles, TrendingUp, History } from 'lucide-react';
import { useRecommendations, useSimilarProducts, RecommendedProduct } from '@/hooks/useRecommendations';

interface ProductCardProps {
  product: RecommendedProduct;
  showBadge?: boolean;
}

function ProductCard({ product, showBadge = true }: ProductCardProps) {
  const getBadgeInfo = (type?: string) => {
    switch (type) {
      case 'based_on_likes':
        return { label: 'Because you liked', icon: Heart, variant: 'default' as const };
      case 'based_on_views':
        return { label: 'Recently viewed', icon: History, variant: 'secondary' as const };
      case 'trending':
        return { label: 'Trending', icon: TrendingUp, variant: 'outline' as const };
      default:
        return { label: 'Popular', icon: Sparkles, variant: 'outline' as const };
    }
  };

  const badge = getBadgeInfo(product.recommendation_type);
  const BadgeIcon = badge.icon;

  return (
    <Link to={`/products/${product.id}`}>
      <Card className="group overflow-hidden hover:shadow-lg transition-all duration-300 h-full">
        <div className="aspect-square relative overflow-hidden bg-muted">
          {product.images?.[0] ? (
            <img
              src={product.images[0]}
              alt={product.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              No image
            </div>
          )}
          {showBadge && product.recommendation_type && (
            <Badge 
              variant="default"
              className="absolute top-2 left-2 text-xs flex items-center gap-1 bg-background/90 text-foreground backdrop-blur-sm border shadow-sm"
            >
              <BadgeIcon className="h-3 w-3" />
              {badge.label}
            </Badge>
          )}
        </div>
        <CardContent className="p-3">
          <h3 className="font-medium text-sm line-clamp-2 group-hover:text-primary transition-colors">
            {product.title}
          </h3>
          <p className="text-primary font-bold mt-1">
            {product.currency || '₦'}{product.price.toLocaleString()}
          </p>
          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Eye className="h-3 w-3" />
              {product.views || 0}
            </span>
            <span className="flex items-center gap-1">
              <Heart className="h-3 w-3" />
              {product.likes || 0}
            </span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function LoadingSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i} className="overflow-hidden">
          <Skeleton className="aspect-square" />
          <CardContent className="p-3 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-5 w-1/2" />
            <Skeleton className="h-3 w-1/3" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

interface RecommendationSectionProps {
  title?: string;
  subtitle?: string;
  limit?: number;
}

export function RecommendedForYou({ title = "Recommended For You", subtitle, limit = 8 }: RecommendationSectionProps) {
  const { products, loading, error } = useRecommendations(limit);

  if (error || (!loading && products.length === 0)) return null;

  return (
    <section className="py-8">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="h-5 w-5 text-primary" />
        <h2 className="text-xl font-bold">{title}</h2>
      </div>
      {subtitle && <p className="text-muted-foreground text-sm mb-4">{subtitle}</p>}
      
      {loading ? (
        <LoadingSkeleton count={limit} />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      )}
    </section>
  );
}

interface SimilarProductsProps {
  productId: string;
  title?: string;
  limit?: number;
}

export function SimilarProducts({ productId, title = "You May Also Like", limit = 4 }: SimilarProductsProps) {
  const { products, loading } = useSimilarProducts(productId, limit);

  if (!loading && products.length === 0) return null;

  return (
    <section className="py-8 border-t">
      <h2 className="text-xl font-bold mb-4">{title}</h2>
      
      {loading ? (
        <LoadingSkeleton count={limit} />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} showBadge={false} />
          ))}
        </div>
      )}
    </section>
  );
}
